/**
 * zinnen.js: tekst in zinnen knippen voor het voorlezen (dyslexie, 09-10-2026).
 *
 * De voorlezer leest zin voor zin en markeert de zin die klinkt. Daarvoor
 * moet de tekst netjes in zinnen worden gedeeld:
 *   - knippen op . ! ? en op een regeleinde;
 *   - NIET bij afkortingen (bijv., d.w.z., nr., Mr., z.B., usw. ...);
 *   - NIET bij decimalen (3.5) of nummering aan het begin ("1. Lees de tekst");
 *   - een formule tussen $...$ of $$...$$ blijft heel, ook met punten erin.
 *
 * Elke zin heeft een id (volgnummer), de tekst zonder witruimte eromheen en
 * de positie (start, eind) in de oorspronkelijke tekst.
 */

// Afkortingen (zonder de laatste punt, kleine letters). Dotted afkortingen
// zoals "d.w.z" worden ook herkend via het patroon INITIALEN hieronder, maar
// staan hier voor de duidelijkheid toch bij.
// Gewone woorden die ook een afkorting kunnen zijn ("zo", "jan", "no") staan
// er bewust NIET in: "Doe het zo." en "Hij heet Jan." zijn echte zinseinden.
const AFKORTINGEN = new Set([
  // Nederlands
  'bijv', 'bv', 'd.w.z', 'nr', 'o.a', 'e.d', 'm.b.t', 'enz', 'ca', 'jl', 'dhr', 'mevr', 'mw',
  'a.u.b', 'z.o.z', 'etc', 'blz', 'pag', 'evt', 'i.p.v', 'i.v.m', 't.o.v', 'm.a.w', 'n.a.v',
  'o.m', 'incl', 'excl', 'ong', 'fig', 'afb', 'hfst', 'par',
  // Engels
  'e.g', 'i.e', 'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'vs', 'approx', 'cf',
  // Duits
  'z.b', 'd.h', 'usw', 'bzw', 'ggf', 'evtl', 'u.a', 'vgl', 'hr', 'fr',
  // Frans
  'p.ex', 'c.-à-d', 'c-à-d', 'mme', 'mlle', 'env',
  // Spaans
  'p.ej', 'sra', 'srta', 'ud', 'uds', 'aprox', 'pág',
]);

// Eén letter of letters-met-punten ("d.w.z", "a.u.b", "J" in "J. Smit").
const INITIALEN = /^(?:\p{L}\.)*\p{L}$/u;
// Nummering aan het begin van een zin: "1." "12." "a." "IV."
const NUMMERING = /^(?:\d{1,3}|[a-zA-Z]|[ivxIVX]{1,5})$/;

const SLUITTEKENS = new Set(['"', "'", ')', ']', '»', '”', '’', '»']);
const EINDTEKENS = new Set(['.', '!', '?']);

// Zelfde regel als formuleTekst.js (pandoc): $...$ begint met een niet-spatie
// na de $ en eindigt met een niet-spatie voor de $; \$ is letterlijk.
// Zonder regex-lookbehind (Safari/iPadOS < 16.4), zie geenLookbehind.test.js.
export const FORMULE_REGEX = /\$\$([\s\S]+?)\$\$|\$(?=\S)((?:\\.|[^$\\])*?(?:\\\S|[^$\\\s]))\$/g;

/** Posities [start, eind) van formules, zodat we daar nooit in knippen. */
function formuleBereiken(tekst) {
  const bereiken = [];
  FORMULE_REGEX.lastIndex = 0;
  let m;
  while ((m = FORMULE_REGEX.exec(tekst)) !== null) {
    if (m.index > 0 && tekst[m.index - 1] === '\\') {
      FORMULE_REGEX.lastIndex = m.index + 1;
      continue;
    }
    bereiken.push([m.index, m.index + m[0].length]);
  }
  return bereiken;
}

function maakBeschermd(tekst) {
  const beschermd = new Uint8Array(tekst.length);
  for (const [a, b] of formuleBereiken(tekst)) beschermd.fill(1, a, b);
  return beschermd;
}

function isWit(ch) {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === ' ';
}

function isCijfer(ch) {
  return ch >= '0' && ch <= '9';
}

/**
 * Het woord vlak voor positie i (de punt), zonder omringende leestekens
 * zoals een openingshaakje of aanhalingsteken.
 */
function woordVoor(tekst, i) {
  let j = i;
  while (j > 0 && !isWit(tekst[j - 1])) j--;
  return tekst.slice(j, i).replace(/^[("'“‘«[]+/, '');
}

/** Begint het volgende woord (vanaf positie i, witruimte overslaand) met een kleine letter? */
function begintMetKleineLetter(tekst, i) {
  let j = i;
  while (j < tekst.length && isWit(tekst[j]) && tekst[j] !== '\n') j++;
  const ch = tekst[j];
  if (!ch || ch === '\n') return false;
  return ch !== ch.toUpperCase() && ch === ch.toLowerCase();
}

/** Is de punt op positie i het einde van een zin? */
function isZinEinde(tekst, i, zinStart) {
  const ch = tekst[i];
  const volgend = tekst[i + 1];
  if (ch === '!' || ch === '?') {
    // "?!" of "!!" samen nemen: pas knippen bij de laatste
    return !(volgend && EINDTEKENS.has(volgend));
  }
  // Punt. Direct gevolgd door iets anders dan witruimte of een sluitteken:
  // decimaal (3.5), webadres, "e.g." (de volgende punt beslist) of "...".
  if (volgend !== undefined && !isWit(volgend) && !SLUITTEKENS.has(volgend)) return false;
  const woord = woordVoor(tekst, i);
  if (!woord) return false;
  const laag = woord.toLowerCase();
  if (AFKORTINGEN.has(laag)) return false;
  if (INITIALEN.test(woord)) return false;
  // Nummering: "1." of "a." als eerste woord van de zin.
  if (NUMMERING.test(woord)) {
    const ervoor = tekst.slice(zinStart, i - woord.length);
    if (ervoor.trim() === '') return false;
  }
  // "Het is 3." mag wel eindigen; een getal midden in een opsomming
  // ("stap 2. kies") kan niet van een zinseinde worden onderscheiden.
  return true;
}

/**
 * Splitst tekst in zinnen.
 * @param {string} tekst
 * @returns {{ id: number, tekst: string, start: number, eind: number }[]}
 */
export function splitsInZinnen(tekst) {
  const bron = typeof tekst === 'string' ? tekst : (tekst == null ? '' : String(tekst));
  if (!bron.trim()) return [];
  const beschermd = maakBeschermd(bron);
  const zinnen = [];
  let start = 0;
  let i = 0;
  const n = bron.length;

  const sluitAf = (eind) => {
    // Witruimte aan het begin en eind weglaten; lege stukken overslaan.
    let a = start;
    let b = eind;
    while (a < b && isWit(bron[a])) a++;
    while (b > a && isWit(bron[b - 1])) b--;
    if (b > a) zinnen.push({ id: zinnen.length, tekst: bron.slice(a, b), start: a, eind: b });
    start = eind;
  };

  while (i < n) {
    const ch = bron[i];
    if (beschermd[i]) { i++; continue; }
    if (ch === '\n') {
      sluitAf(i);
      i++;
      start = i;
      continue;
    }
    if (EINDTEKENS.has(ch) && !(ch === '.' && isCijfer(bron[i + 1] || '') && isCijfer(bron[i - 1] || ''))) {
      if (isZinEinde(bron, i, start)) {
        let eind = i + 1;
        // Sluittekens horen bij de zin: Hij zei "stop."  → de " hoort erbij.
        while (eind < n && SLUITTEKENS.has(bron[eind])) eind++;
        // Gaat de tekst verder met een kleine letter ("(echt waar!) ging hij"),
        // dan loopt de zin door.
        if (begintMetKleineLetter(bron, eind)) { i = eind; continue; }
        sluitAf(eind);
        i = eind;
        continue;
      }
    }
    i++;
  }
  sluitAf(n);
  return zinnen;
}

/**
 * Zelfde als splitsInZinnen, maar met ids als prefix + '-' + volgnummer,
 * bijv. 'vraag-0' of 'optie-2-1'. Handig als één scherm meerdere tekstblokken heeft.
 */
export function zinnenVanBlok(tekst, prefix) {
  const p = prefix == null ? '' : String(prefix);
  return splitsInZinnen(tekst).map((z) => ({ ...z, id: p ? `${p}-${z.id}` : String(z.id) }));
}
